/**
 * PenPowerSignSDK.ts
 * A modern, zero-dependency TypeScript client library for PenPower Signature Pads.
 * Exposes a clean, promise-based, type-safe API to communicate with the local PenPower driver.
 */

export interface PenPowerDevice {
  id: number;
  name: string;
}

export interface PenPowerSignSDKOptions {
  /** Base URL of the PenPower driver (default: 'http://localhost:8089/PPSignSDK/') */
  apiUrl?: string;
  /** Optional HTML canvas element to render the signature in real time */
  canvas?: HTMLCanvasElement | null;
  /** Callback when drawing ink updates. Receives raw base64 drawing data (without mime prefix) */
  onInk?: ((base64Image: string) => void) | null;
  /** Callback when physical Confirm key is pressed. Receives latest base64 drawing data */
  onConfirm?: ((lastBase64Image: string | null) => void) | null;
  /** Callback when physical Cancel key is pressed */
  onCancel?: (() => void) | null;
  /** Callback when signature pad connection status changes */
  onStatusChange?: ((status: { connected: boolean; device?: PenPowerDevice }) => void) | null;
  /** Callback when polling or connection errors occur */
  onError?: ((error: Error) => void) | null;
  /** Enable detailed logging for debugging connection and polling sequences */
  debug?: boolean;
}

export interface PenPowerExportOptions {
  /** Output file format ('png', 'jpeg', 'webp', or 'svg'). Defaults to 'png' */
  format?: 'png' | 'jpeg' | 'webp' | 'svg' | string;
  /** Image quality ratio from 0.0 to 1.0 (applicable for jpeg and webp). Defaults to 0.92 */
  quality?: number;
  /** Automatically crop empty white/transparent margins around the signature. Defaults to true */
  trim?: boolean;
  /** Export image with transparent background (applicable for png, webp, svg). Defaults to true */
  transparent?: boolean;
  /** Opaque background color (ignored if `transparent` is true). Defaults to '#ffffff' */
  backgroundColor?: string;
  /** Hex, RGB, or HTML color string to tint the ink strokes (e.g. 'blue' or '#0000ff'). Defaults to null (original) */
  tintColor?: string | null;
  /** Output representation: 'dataURL' or 'blob'. Defaults to 'dataURL' */
  outputType?: 'dataURL' | 'blob' | string;
}

interface InkEvent {
  EventType: number;
  Image: string;
}

interface ColorRGB {
  r: number;
  g: number;
  b: number;
}

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class PenPowerSignSDK {
  public readonly apiUrl: string;
  public canvas: HTMLCanvasElement | null;
  public onInk: ((base64Image: string) => void) | null;
  public onConfirm: ((lastBase64Image: string | null) => void) | null;
  public onCancel: (() => void) | null;
  public onStatusChange: ((status: { connected: boolean; device?: PenPowerDevice }) => void) | null;
  public onError: ((error: Error) => void) | null;
  public readonly debug: boolean;

  public isPolling: boolean = false;
  public connectedDevice: PenPowerDevice | null = null;
  public lastSignatureBase64: string | null = null;

  public readonly devices: PenPowerDevice[] = [
    { id: 2, name: 'Default Device (ID 2)' },
    { id: 5, name: 'L500 Signature Pad' },
    { id: 7, name: 'L501G Signature Pad' },
    { id: 3, name: 'LC700 Signature Pad' },
    { id: 4, name: 'E640 Signature Pad' }
  ];

  /**
   * Create a PenPower signature client.
   * @param options Configuration options.
   */
  constructor(options: PenPowerSignSDKOptions = {}) {
    this.apiUrl = options.apiUrl || 'http://localhost:8089/PPSignSDK/';
    this.canvas = options.canvas || null;
    this.onInk = options.onInk || null;
    this.onConfirm = options.onConfirm || null;
    this.onCancel = options.onCancel || null;
    this.onStatusChange = options.onStatusChange || null;
    this.onError = options.onError || null;
    this.debug = !!options.debug;

    this._log('SDK instance initialized with options:', { apiUrl: this.apiUrl, debug: this.debug });

    // Automatically release lock on page unload/refresh
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        if (this.connectedDevice) {
          this._log(`Page unload detected. Auto-releasing connection for device ID ${this.connectedDevice.id}...`);
          const url = `${this.apiUrl}UninitialDevice?id=${this.connectedDevice.id}`;
          try {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, false); // synchronous request to ensure it completes before tab closes
            xhr.send();
            this._log('Auto-release request sent successfully via sync XHR.');
          } catch (e) {
            // Fallback to modern keepalive fetch if sync XHR is blocked by modern browser policy
            fetch(url, { method: 'GET', keepalive: true, mode: 'no-cors' });
            this._log('Auto-release request sent via fetch keepalive fallback.');
          }
        }
      });
    }
  }

  /**
   * Initialize and connect to the signature pad.
   * Cycles through device IDs until a device is successfully opened.
   * @param width Device canvas width coordinate matching.
   * @param height Device canvas height coordinate matching.
   * @returns The connected device info.
   */
  public async connect(width: number = 580, height: number = 380): Promise<PenPowerDevice> {
    this._log(`Attempting connection sequence with canvas dimensions: ${width}x${height}`);
    if (this.connectedDevice) {
      this._log(`Already connected to device: ${this.connectedDevice.name}`);
      return this.connectedDevice;
    }

    let isDriverRunning = false;

    for (const device of this.devices) {
      try {
        this._log(`Probing device: ID ${device.id} (${device.name})`);
        
        // 1. Try to release any previous stale lock on this device ID first
        try {
          this._log(`Releasing any active connection lock on device ID ${device.id} before init...`);
          await fetch(`${this.apiUrl}UninitialDevice?id=${device.id}`, { method: 'GET', cache: 'no-cache' });
          await new Promise(resolve => setTimeout(resolve, 300)); // Give hardware time to release
        } catch (e) {
          // Ignore error since it might not be locked in the first place
        }

        // 2. Try to initialize the device
        const url = `${this.apiUrl}InitialDevice?id=${device.id}&width=${width}&height=${height}`;
        this._log(`Initializing device via url: ${url}`);
        const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
        
        isDriverRunning = true; // Received response, so driver is active
        const text = await response.text();
        const result = text.trim().toLowerCase() === 'true';
        this._log(`Device ID ${device.id} returned initialization result:`, result);

        if (result) {
          this.connectedDevice = device;
          this.isPolling = true;
          this._log(`Successfully connected and bound to device: ${device.name}`);
          this._startPolling();
          
          if (this.onStatusChange) {
            this.onStatusChange({ connected: true, device });
          }
          return device;
        }
      } catch (err) {
        this._logError(`Probe failed for device ID ${device.id}:`, err);
      }
    }

    if (!isDriverRunning) {
      this._logError(`PenPower driver service offline. Failed connection to port 8089.`);
      const error = new Error('PenPower driver service is not running on port 8089. Please ensure the PenPower driver is installed and running.');
      if (this.onError) this.onError(error);
      throw error;
    }

    this._logError('Device probe complete: No signature pad detected on USB ports.');
    const error = new Error('No PenPower signature pad detected. Please verify your signature pad USB connection.');
    if (this.onError) this.onError(error);
    throw error;
  }

  /**
   * Disconnect and uninitialize the active device.
   * @returns True if disconnected successfully.
   */
  public async disconnect(): Promise<boolean> {
    if (!this.connectedDevice) {
      this._log('Disconnect requested, but no device is connected.');
      return false;
    }

    this._log(`Disconnecting from active device: ${this.connectedDevice.name}...`);
    this.isPolling = false;
    const device = this.connectedDevice;
    
    try {
      const url = `${this.apiUrl}UninitialDevice?id=${device.id}`;
      this._log(`Sending UninitialDevice request: ${url}`);
      const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
      const text = await response.text();
      const result = text.trim().toLowerCase() === 'true';
      this._log(`UninitialDevice response:`, result);

      if (result) {
        this.connectedDevice = null;
        this.lastSignatureBase64 = null;
        this.clearCanvas();
        this._log('Disconnected cleanly.');
        
        if (this.onStatusChange) {
          this.onStatusChange({ connected: false });
        }
        return true;
      }
    } catch (err) {
      this._logError('Error during device disconnection:', err);
      if (err instanceof Error && this.onError) this.onError(err);
    }
    
    // Force clean up local state even if server request fails
    this._log('Forcing local state cleanup after disconnect error.');
    this.connectedDevice = null;
    this.lastSignatureBase64 = null;
    if (this.onStatusChange) {
      this.onStatusChange({ connected: false });
    }
    return false;
  }

  /**
   * Clear signature ink on both hardware device and browser canvas.
   * @returns True if clear succeeded.
   */
  public async clear(): Promise<boolean> {
    this._log('Requesting canvas and hardware buffer clear...');
    try {
      const url = `${this.apiUrl}Clear`;
      const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
      if (response.ok) {
        this._log('Hardware buffer cleared successfully.');
        this.lastSignatureBase64 = null;
        this.clearCanvas();
        return true;
      }
    } catch (err) {
      this._logError('Failed to clear signature pad:', err);
      if (err instanceof Error && this.onError) this.onError(err);
    }
    return false;
  }

  /**
   * Clear the local browser canvas context.
   */
  public clearCanvas(): void {
    if (this.canvas) {
      const ctx = this.canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
    }
  }

  /**
   * Trigger local driver backend to save the image (legacy server feature).
   * @param options File save parameters.
   * @returns Path of the saved image file on the local computer.
   */
  public async saveLocalDrawingFile(options: { type?: number; dpi?: number; filename?: string } = {}): Promise<string> {
    const type = options.type !== undefined ? options.type : 2;
    const dpi = options.dpi !== undefined ? options.dpi : 150;
    const filename = options.filename || `DrawImage_${Math.floor(10000 + Math.random() * 90000)}`;
    
    let extension = '.JPG';
    if (type === 1) extension = '.BMP';
    else if (type === 3) extension = '.GIF';
    else if (type === 4) extension = '.PNG';
    else if (type === 5) extension = '.TIF';

    const url = `${this.apiUrl}SaveDrawingImage?type=${type}&dpi=${dpi}&path=${filename}${extension}`;
    
    try {
      const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
      if (response.ok) {
        return await response.text(); // Returns path or error indicator
      }
    } catch (err) {
      if (err instanceof Error && this.onError) this.onError(err);
      throw err;
    }
    throw new Error('Failed to request server-side save');
  }

  /**
   * Start polling loops for signature ink and button presses.
   * @private
   */
  private _startPolling(): void {
    this._pollInk();
    this._pollKeys();
  }

  /**
   * Long-polling loop for drawing update events.
   * @private
   */
  private async _pollInk(): Promise<void> {
    this._log('Starting drawing ink polling loop (50ms interval)...');
    while (this.isPolling) {
      try {
        const url = `${this.apiUrl}GetInks`;
        const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
        
        if (response.ok) {
          const text = await response.text();
          if (text) {
            let dataInfos = JSON.parse(text);
            // Handle double-encoded JSON string returned by some server configurations
            if (typeof dataInfos === 'string') {
              dataInfos = JSON.parse(dataInfos);
            }
            
            if (Array.isArray(dataInfos) && dataInfos.length > 0) {
              this._log(`GetInks retrieved ${dataInfos.length} events.`);
              for (const value of dataInfos as InkEvent[]) {
                // EventType 0 represents ink drawing updates
                if (value.EventType === 0 && value.Image) {
                  this._log(`Drawing event received. Redrawing canvas. Base64 length: ${value.Image.length}`);
                  this.lastSignatureBase64 = value.Image;
                  if (this.canvas) {
                    this._drawImageToCanvas(value.Image);
                  }
                  if (this.onInk) {
                    this.onInk(value.Image);
                  }
                }
              }
            }
          }
        }
      } catch (err) {
        this._logError('Ink polling error:', err);
      }
      
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    this._log('Drawing ink polling loop stopped.');
  }

  /**
   * Long-polling loop for physical Confirm/Cancel button actions.
   * @private
   */
  private async _pollKeys(): Promise<void> {
    this._log('Starting key status polling loop (500ms interval)...');
    while (this.isPolling) {
      try {
        const url = `${this.apiUrl}GetDeviceConfirmOrCancelKeyStatus`;
        const response = await fetch(url, { method: 'GET', cache: 'no-cache' });
        
        if (response.ok) {
          const result = await response.text();
          // Strip any surrounding double/single quotes returned by the server
          const clean = result ? result.replace(/['"]/g, '').trim() : '';
          
          if (clean === '1') {
            this._log('Physical Confirm key pressed event detected.');
            // Physical Confirm button pressed
            if (this.onConfirm) {
              this.onConfirm(this.lastSignatureBase64);
            }
            await this.clear();
          } else if (clean === '0') {
            this._log('Physical Cancel key pressed event detected.');
            // Physical Cancel button pressed
            await this.clear();
            if (this.onCancel) {
              this.onCancel();
            }
          }
        }
      } catch (err) {
        this._logError('Key status polling error:', err);
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    this._log('Key status polling loop stopped.');
  }

  /**
   * Render base64 image data onto the canvas.
   * @param base64 Base64 string from the API (without prefix).
   * @private
   */
  private _drawImageToCanvas(base64: string): void {
    const dataUrl = `data:image/png;base64,${base64}`;
    const img = new Image();
    img.onload = () => {
      if (this.canvas) {
        const ctx = this.canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, this.canvas.width, this.canvas.height);
        }
      }
    };
    img.src = dataUrl;
  }

  /**
   * Parse hex, rgb, or common color name into { r, g, b } components.
   * @param colorStr Color definition.
   * @private
   */
  private _parseColor(colorStr: string): ColorRGB {
    const defaultColor: ColorRGB = { r: 0, g: 0, b: 0 };
    if (!colorStr) return defaultColor;

    const normalized = colorStr.trim().toLowerCase();

    // Hex syntax (#ffffff or #fff)
    if (normalized.startsWith('#')) {
      const hex = normalized.substring(1);
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16)
        };
      } else if (hex.length === 6) {
        return {
          r: parseInt(hex.substring(0, 2), 16),
          g: parseInt(hex.substring(2, 4), 16),
          b: parseInt(hex.substring(4, 6), 16)
        };
      }
    }

    // RGB syntax (rgb(0, 0, 255))
    const rgbMatch = normalized.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
    if (rgbMatch) {
      return {
        r: parseInt(rgbMatch[1], 10),
        g: parseInt(rgbMatch[2], 10),
        b: parseInt(rgbMatch[3], 10)
      };
    }

    // Common standard colors
    const colors: Record<string, ColorRGB> = {
      black: { r: 0, g: 0, b: 0 },
      white: { r: 255, g: 255, b: 255 },
      blue: { r: 0, g: 0, b: 255 },
      red: { r: 255, g: 0, b: 0 },
      green: { r: 0, g: 128, b: 0 }
    };
    return colors[normalized] || defaultColor;
  }

  /**
   * Find the bounding box containing ink drawing on a canvas to trim margins.
   * @param imgData Pixel data.
   * @param bgIsWhite True if empty space is white. False if empty space is transparent.
   * @private
   */
  private _getBoundingBox(imgData: ImageData, bgIsWhite: boolean = true): BoundingBox {
    const { width, height } = imgData;
    let minX = width;
    let maxX = 0;
    let minY = height;
    let maxY = 0;
    let found = false;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = (y * width + x) * 4;
        const r = imgData.data[idx];
        const g = imgData.data[idx + 1];
        const b = imgData.data[idx + 2];
        const a = imgData.data[idx + 3];

        let isContent = false;
        if (bgIsWhite) {
          isContent = a > 10 && !(r > 240 && g > 240 && b > 240);
        } else {
          isContent = a > 10;
        }

        if (isContent) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          found = true;
        }
      }
    }

    if (!found) {
      return { x: 0, y: 0, width, height };
    }

    const padding = 12;
    const x = Math.max(0, minX - padding);
    const y = Math.max(0, minY - padding);
    const w = Math.min(width - x, (maxX - minX + 1) + padding * 2);
    const h = Math.min(height - y, (maxY - minY + 1) + padding * 2);

    return { x, y, width: w, height: h };
  }

  /**
   * Export the current signature canvas into custom image formats with processing.
   * Supports changing background opacity/color, changing ink color, trimming margins, and format selection.
   * 
   * @param options Export options.
   * @returns The resulting image data URL, Blob, or SVG XML.
   */
  public async export(options: PenPowerExportOptions = {}): Promise<string | Blob> {
    this._log('Running export image processing with parameters:', options);
    if (!this.canvas) {
      throw new Error('No canvas is bound to this SDK client instance.');
    }

    const format = (options.format || 'png').toLowerCase();
    const quality = options.quality !== undefined ? options.quality : 0.92;
    const trim = options.trim !== undefined ? !!options.trim : true;
    const transparent = options.transparent !== undefined ? !!options.transparent : true;
    const backgroundColor = options.backgroundColor || '#ffffff';
    const tintColor = options.tintColor || null;
    const outputType = (options.outputType || 'dataURL').toLowerCase();

    const srcWidth = this.canvas.width;
    const srcHeight = this.canvas.height;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not retrieve 2D rendering context from the canvas.');
    }
    
    const srcData = ctx.getImageData(0, 0, srcWidth, srcHeight);
    
    let box: BoundingBox = { x: 0, y: 0, width: srcWidth, height: srcHeight };
    if (trim) {
      box = this._getBoundingBox(srcData, true);
    }

    const destCanvas = document.createElement('canvas');
    destCanvas.width = box.width;
    destCanvas.height = box.height;
    const destCtx = destCanvas.getContext('2d');
    if (!destCtx) {
      throw new Error('Could not create destination canvas 2D context.');
    }
    
    const cropData = ctx.getImageData(box.x, box.y, box.width, box.height);
    const destData = destCtx.createImageData(box.width, box.height);

    const parsedTint = tintColor ? this._parseColor(tintColor) : null;
    const parsedBg = this._parseColor(backgroundColor);

    for (let i = 0; i < cropData.data.length; i += 4) {
      const r = cropData.data[i];
      const g = cropData.data[i + 1];
      const b = cropData.data[i + 2];
      const a = cropData.data[i + 3];

      const density = (1.0 - ((r + g + b) / 3 / 255)) * (a / 255);

      let outR = r;
      let outG = g;
      let outB = b;
      let outA = a;

      if (transparent) {
        if (parsedTint) {
          outR = parsedTint.r;
          outG = parsedTint.g;
          outB = parsedTint.b;
        }
        outA = Math.round(density * 255);
      } else {
        const inkR = parsedTint ? parsedTint.r : r;
        const inkG = parsedTint ? parsedTint.g : g;
        const inkB = parsedTint ? parsedTint.b : b;

        outR = Math.round(inkR * density + parsedBg.r * (1 - density));
        outG = Math.round(inkG * density + parsedBg.g * (1 - density));
        outB = Math.round(inkB * density + parsedBg.b * (1 - density));
        outA = 255;
      }

      destData.data[i] = outR;
      destData.data[i + 1] = outG;
      destData.data[i + 2] = outB;
      destData.data[i + 3] = outA;
    }

    destCtx.putImageData(destData, 0, 0);

    if (format === 'svg') {
      const pngDataURL = destCanvas.toDataURL('image/png');
      const svgString = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${box.width}" height="${box.height}" viewBox="0 0 ${box.width} ${box.height}">
  <image href="${pngDataURL}" width="${box.width}" height="${box.height}" />
</svg>`;
      return svgString;
    }

    const mime = `image/${format === 'jpg' ? 'jpeg' : format}`;
    
    if (outputType === 'blob') {
      return new Promise<Blob>((resolve) => {
        destCanvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(new Blob());
          }
        }, mime, quality);
      });
    }

    return destCanvas.toDataURL(mime, quality);
  }

  /**
   * Log info message to the console if debug is enabled.
   */
  private _log(message: string, ...args: any[]): void {
    if (this.debug) {
      console.log(`[PenPowerSignSDK] ${message}`, ...args);
    }
  }

  /**
   * Log error message to the console if debug is enabled.
   */
  private _logError(message: string, ...args: any[]): void {
    if (this.debug) {
      console.error(`[PenPowerSignSDK] ERROR: ${message}`, ...args);
    }
  }
}
